import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { toastVariant } from '__components/ToastProvider';
import { useToast } from '__components/ToastProvider';
import { JSONEditor } from '__pages/Marketplace/components/JSONEditor';
import { ValidationSizer } from '__pages/Marketplace/styles';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PluginService from 'services/PluginService';

type JSONEditorProps = {
  config: Record<string, any>;
  refetch: () => Promise<unknown>;
  plugin: MarketplacePlugin;
};

export const JSONEditorInline = ({
  config,
  refetch,
  plugin,
}: JSONEditorProps) => {
  const [value, setValue] = useState(() => JSON.stringify(config, null, 2));
  const [pending, setPending] = useState(false);
  const [showToast] = useToast();

  const { t } = useTranslation();

  const { isValid, parsed } = useMemo(() => {
    try {
      const parsed = JSON.parse(value);
      return { isValid: true, parsed };
    } catch (e) {
      return { isValid: false, parsed: {} };
    }
  }, [value]);

  const onChangeRawJSONConfig = useCallback(
    async (config: any) => {
      try {
        setPending(true);

        await PluginService.updateBusinessPluginApp(plugin.api_name, {
          config,
        });

        await refetch();

        showToast({
          message: t('Configuration saved successfully'),
          variant: toastVariant.SUCCESS,
        });
      } catch (ex: any) {
        showToast({
          message: ex?.message || t('Failed to save configuration'),
          variant: toastVariant.FAILURE,
        });

        monitoringExceptionHelper(ex);
      } finally {
        setPending(false);
      }
    },
    [refetch, plugin, showToast, t]
  );

  return (
    <>
      <JSONEditor value={value} onChange={setValue} />
      <Spacer size={5} mode="horizontal" />
      <ValidationSizer className="flex">
        {isValid ? (
          <>
            <Typography color="feedback-toast-success" className="ml-auto">
              {t('JSON is valid')}
            </Typography>
          </>
        ) : (
          <>
            <Typography color="feedback-toast-error" className="ml-auto">
              {t('JSON is invalid')}
            </Typography>
          </>
        )}
      </ValidationSizer>
      <div className="flex">
        <div className="ml-auto">
          <Button
            disabled={!isValid || pending}
            onClick={() => onChangeRawJSONConfig(parsed)}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    </>
  );
};
